/**
 * Import d'exports de gestionnaires concurrents au-dela du CSV simple :
 * 1Password `.1pux` (archive ZIP contenant `export.data`, JSON) et archives
 * ZIP de CSV (Dashlane). Purement local, aucune E/S reseau : ce module
 * transforme des octets choisis par l'utilisateur en `ImportedEntry`, que
 * l'UI chiffre ensuite une par une (`VaultStore.createEntry`).
 *
 * `parseImportFile` est l'AIGUILLAGE unique de tous les imports concurrents :
 * KeePass KDBX 4 et 3.1 (`kdbx-import.ts`), Bitwarden JSON chiffre ou non
 * (`bitwarden-import.ts`), Proton Pass (`proton-import.ts`), FIDO CXF chiffre
 * ou nu (`cxf.ts`), 1Password `.1pux` et `.1pif` (`onepif-import.ts`), archives de CSV et CSV simple
 * (`csv-import.ts`).
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import type { VaultItemType } from '@klavox/vault-model';
import { VAULT_EXPORT_FORMAT } from './vault-export.js';
import { isAegisExport, isAuthenticatorUriList, isProtonAuthenticatorExport, isTwoFasExport, parseAegisExport, parseAuthenticatorUriList, parseProtonAuthenticatorExport, parseTwoFasExport } from './authenticator-import.js';
import { isAvastExport, parseAvastExport } from './avast-import.js';
import { isBitwardenExport, readBitwardenExport } from './bitwarden-import.js';
import { isCxfExportFile, isCxfPayload, parseCxfImport } from './cxf.js';
import { isEnpassExport, parseEnpassExport } from './enpass-import.js';
import { isKasperskyTxt, parseKasperskyTxt } from './kaspersky-import.js';
import { isKdbFile, isKdbxFile, isKeePassXml, readKdbFile, readKdbxFile, readKeePassXml } from './kdbx-import.js';
import { isKeeperExport, parseKeeperExport } from './keeper-import.js';
import { isFSecureExport, parseFSecureExport } from './fsecure-import.js';
import { isOnePifText, onePifItems } from './onepif-import.js';
import { isPasskyExport, parsePasskyExport } from './passky-import.js';
import { isPassmanExport, parsePassmanExport } from './passman-import.js';
import { isPasswordBossExport, parsePasswordBossExport } from './passwordboss-import.js';
import { isDashlaneJsonExport, parseDashlaneJsonExport } from './dashlane-json-import.js';
import { isPsonoExport, parsePsonoExport } from './psono-import.js';
import { isPasswordDepotXml, parsePasswordDepotXml } from './passworddepot-import.js';
import { isPasswordSafeXml, parsePasswordSafeXml } from './passwordsafe-import.js';
import { isDelineaXml, readDelineaXml } from './delinea-import.js';
import { isPasswordDragonXml, parsePasswordDragonXml } from './passworddragon-import.js';
import { isPasswordXpCsv, parsePasswordXpCsv } from './passwordxp-import.js';
import { parseExplicitFormat, type ImportFormat } from './headerless-import.js';
import { isGnomeKeyringExport, parseGnomeKeyringExport } from './gnome-import.js';
import { isClipperzHtml, parseClipperzHtml } from './clipperz-import.js';
import { isSafeInCloudXml, parseSafeInCloudXml } from './safeincloud-import.js';
import { isStickyPasswordXml, parseStickyPasswordXml } from './stickypassword-import.js';
import { isProtonExport, readProtonExport } from './proton-import.js';
import { joinNotes, normalizeTotpSecret, parseLoginCsv, type ImportedAttachment, type ImportedEntry } from './csv-import.js';
import { normalizeTags } from './entry-tags.js';
import type { PasswordHistoryEntry } from './password-history.js';
import type { SshKeyMaterial } from './ssh-key-item.js';
import { bankAccountType, databaseEngine, emailProtocol, emailSecurity, normalizeTypedFields, typedFieldGroup, wifiSecurity } from './typed-fields.js';
import { readZipEntries } from './zip.js';

/** Categories 1Password (`categoryUuid`) ; toute autre categorie devient une note. */
const ONEPASSWORD_CATEGORIES: Record<string, VaultItemType> = {
  '001': 'login',
  '002': 'card',
  '003': 'note',
  '004': 'identity',
  '005': 'login',
  '101': 'bank',
  '102': 'database',
  '103': 'identity',
  '106': 'identity',
  '108': 'identity',
  '109': 'wifi',
  '112': 'api-key',
  '100': 'software-license',
  '110': 'server',
  '111': 'email',
  '105': 'membership',
  '107': 'membership',
  '113': 'medical',
  '114': 'ssh-key',
  '115': 'wallet',
};

/** `id` des champs de section 1Password -> champ type (`typed-fields.ts`), par categorie. */
const ONEPASSWORD_TYPED_FIELDS: Record<string, Record<string, string>> = {
  '002': { cardholder: 'cardholder', ccnum: 'number', cvv: 'cvv', expiry: 'expiry', pin: 'pin', bank: 'bank' },
  '004': {
    firstname: 'firstName',
    lastname: 'lastName',
    birthdate: 'birthDate',
    email: 'email',
    defphone: 'phone',
    cellphone: 'phone',
    homephone: 'phone',
    busphone: 'phone',
    company: 'company',
  },
  '103': { number: 'licenseNumber', expiry_date: 'licenseExpiry' },
  '106': { number: 'passportNumber', expiry_date: 'passportExpiry' },
  '108': { number: 'socialSecurityNumber' },
  // Routeur sans fil : nom du reseau et securite (`wpa2p`, `wep`… normalises par `normalizeTypedFields`). La borne
  // elle-meme (nom, mot de passe d'administration, adresse) reste en champs personnalises.
  '109': { network_name: 'ssid', wireless_security: 'security' },
  // Compte bancaire : `owner` dans les exports, `nameOnAccount` dans les modeles de la CLI `op` ; `telephonePin` est le
  // code de la banque par telephone. L'agence (`branchPhone`, `branchAddress`) reste en champs personnalises.
  '101': {
    bankName: 'bankName',
    owner: 'accountHolder',
    nameOnAccount: 'accountHolder',
    accountType: 'accountType',
    routingNo: 'routingNumber',
    accountNo: 'accountNumber',
    swift: 'bic',
    iban: 'iban',
    telephonePin: 'bankPin',
  },
  // Base de donnees : identifiant et mot de passe de connexion vont a l'entree (`ONEPASSWORD_USERNAME_FIELDS`,
  // `ONEPASSWORD_PASSWORD_FIELDS`), le reste a ses champs.
  '102': {
    database_type: 'engine',
    hostname: 'host',
    port: 'port',
    database: 'databaseName',
    sid: 'sid',
    alias: 'connectionAlias',
    options: 'connectionOptions',
  },
  // Cle d'API (« API Credential ») : identifiant et cle vont a l'entree, le reste a ses champs.
  '112': {
    type: 'apiKeyType',
    hostname: 'apiEndpoint',
    filename: 'apiFileName',
    validFrom: 'apiValidFrom',
    expires: 'apiExpiry',
  },
  // Licence logicielle : `reg_code` est la cle. Le site de l'editeur et le prix public restent en champs personnalises.
  '100': {
    reg_code: 'licenseKey',
    product_version: 'productVersion',
    reg_name: 'licensedTo',
    reg_email: 'registeredEmail',
    company: 'licenseCompany',
    download_link: 'downloadUrl',
    publisher_name: 'publisherName',
    support_email: 'supportEmail',
    order_date: 'purchaseDate',
    order_number: 'orderNumber',
    order_total: 'orderTotal',
  },
  // Serveur : `url` est l'adresse du serveur ; identifiant et mot de passe du compte vont a l'entree. La console
  // d'administration et l'hebergeur (`name`, `website`, `support_contact_*`) ont leurs champs.
  '110': {
    url: 'serverUrl',
    admin_console_url: 'adminConsoleUrl',
    admin_console_username: 'adminUsername',
    admin_console_password: 'adminPassword',
    name: 'hostingProvider',
    website: 'hostingWebsite',
    support_contact_url: 'hostingSupportUrl',
    support_contact_phone: 'hostingSupportPhone',
  },
  // Compte de messagerie : `pop_*` decrit le serveur entrant (IMAP ou POP3, malgre son nom), son identifiant et son mot
  // de passe vont a l'entree ; le SMTP et le fournisseur (`provider`, `provider_website`, `phone_*`) ont leurs champs.
  '111': {
    pop_type: 'incomingProtocol',
    pop_server: 'incomingServer',
    pop_port: 'incomingPort',
    pop_security: 'incomingSecurity',
    smtp_server: 'smtpServer',
    smtp_port: 'smtpPort',
    smtp_security: 'smtpSecurity',
    smtp_username: 'smtpUsername',
    smtp_password: 'smtpPassword',
    provider: 'emailProvider',
    provider_website: 'emailProviderWebsite',
    phone_local: 'emailSupportPhone',
    phone_tollfree: 'emailSupportPhone',
  },
  // Adhesion (« Membership ») : `org_name` est le groupe, `membership_no` l'identifiant de membre, `pin` le code masque.
  '105': {
    org_name: 'organization',
    member_name: 'memberName',
    membership_no: 'membershipNumber',
    pin: 'membershipPin',
    member_since: 'memberSince',
    expiry_date: 'membershipExpiry',
    website: 'membershipWebsite',
    phone: 'membershipPhone',
  },
  // Programme de fidelite (« Reward Program ») : memes notions, l'organisme s'appelle `company_name`.
  '107': {
    company_name: 'organization',
    member_name: 'memberName',
    membership_no: 'membershipNumber',
    additional_no: 'additionalNumber',
    pin: 'membershipPin',
    member_since: 'memberSince',
    customer_service_phone: 'membershipPhone',
    reservations_phone: 'reservationsPhone',
    website: 'membershipWebsite',
  },
  // Dossier medical (« Medical Record ») : la section « medication » porte le traitement, `notes` ses remarques.
  '113': {
    date: 'recordDate',
    location: 'medicalFacility',
    healthcareprofessional: 'healthcareProfessional',
    patient: 'patientName',
    reason: 'visitReason',
    medication: 'medication',
    dosage: 'dosage',
    notes: 'medicationNotes',
  },
};

/**
 * Libelle (`title`, en minuscules) des champs de section 1Password -> champ type, par categorie, quand l'`id` n'est pas
 * connu avec certitude. Portefeuille crypto (« Crypto Wallet ») : 1Password documente ses champs par leur libelle
 * (« recovery phrase », « password », « wallet address »), pas par leur identifiant ; un libelle inconnu reste en champ
 * personnalise, jamais perdu. « passphrase » seul est ambigu (mot de passe de l'application ?) : il n'est pas range.
 */
const ONEPASSWORD_TYPED_LABELS: Record<string, Record<string, string>> = {
  '115': {
    'recovery phrase': 'recoveryPhrase',
    'secret recovery phrase': 'recoveryPhrase',
    'seed phrase': 'recoveryPhrase',
    'mnemonic': 'recoveryPhrase',
    'bip39 passphrase': 'seedPassphrase',
    '25th word': 'seedPassphrase',
    'wallet address': 'walletAddress',
    'address': 'walletAddress',
    'public address': 'walletAddress',
    'network': 'blockchain',
    'blockchain': 'blockchain',
    'wallet': 'walletApp',
    'wallet name': 'walletApp',
    'wallet app': 'walletApp',
  },
};

/** Champs types que la categorie fixe d'elle-meme (adhesion ou fidelite : une seule fiche Klavox, deux natures). */
const ONEPASSWORD_CATEGORY_FIELDS: Record<string, Record<string, string>> = {
  '105': { membershipKind: 'membership' },
  '107': { membershipKind: 'reward' },
};

/** Champ de section qui porte le mot de passe de l'entree, par categorie (routeur : la cle du reseau). */
const ONEPASSWORD_PASSWORD_FIELDS: Record<string, string> = { '102': 'password', '109': 'wireless_password', '110': 'password', '111': 'pop_password', '112': 'credential', '115': 'password' };

/** Libelle du champ de section qui porte le mot de passe, par categorie dont les identifiants ne sont pas surs. */
const ONEPASSWORD_PASSWORD_LABELS: Record<string, string> = { '115': 'password' };

/** Champ de section qui porte l'identifiant de l'entree, par categorie (base de donnees : l'utilisateur de connexion). */
const ONEPASSWORD_USERNAME_FIELDS: Record<string, string> = { '102': 'username', '110': 'username', '111': 'pop_username', '112': 'username' };

/** Sous-champs d'une valeur `address` 1Password -> champs d'identite. */
const ONEPASSWORD_ADDRESS_FIELDS: Record<string, string> = { street: 'address', zip: 'postalCode', city: 'city', state: 'region', country: 'country' };

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
type JsonObject = { [key: string]: Json };

function asObject(value: Json | undefined): JsonObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function asArray(value: Json | undefined): Json[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: Json | undefined): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
}

/** Rend lisible la valeur typee d'un champ de section 1Password (`{ "concealed": "..." }`, etc.). */
function describeFieldValue(value: JsonObject): string {
  const [kind, raw] = Object.entries(value)[0] ?? [];
  if (kind === undefined || raw === undefined || raw === null) return '';
  if (kind === 'date' && typeof raw === 'number') return new Date(raw * 1000).toISOString().slice(0, 10);
  if (kind === 'monthYear' && typeof raw === 'number') {
    const text = String(raw);
    return `${text.slice(4)}/${text.slice(0, 4)}`;
  }
  const object = asObject(raw);
  if (object === null) return asString(raw);
  if (kind === 'email') return asString(object['email_address']);
  if (kind === 'sshKey') return asString(object['privateKey']);
  return Object.values(object).map(asString).filter(Boolean).join(', ');
}

/** Fichiers du dossier `files/` d'un `.1pux`, par chemin dans l'archive. */
type OnePuxFiles = ReadonlyMap<string, Uint8Array>;

/**
 * Octets d'un document 1Password (`{ fileName, documentId }`), ranges par l'export sous
 * `files/<documentId>__<fileName>`. On cherche par l'identifiant seul : un nom reecrit par
 * l'archiveur (accents, separateur) ne doit pas faire perdre le fichier.
 */
function findOnePuxFile(files: OnePuxFiles, documentId: string): Uint8Array | undefined {
  if (documentId.length === 0) return undefined;
  const prefix = `files/${documentId}`;
  for (const [name, bytes] of files) {
    // Identifiant suivi d'un separateur (ou seul) : `abc` ne prend pas le fichier de `abcd`.
    if (name === prefix || (name.startsWith(prefix) && !/^[A-Za-z0-9]/u.test(name.slice(prefix.length)))) return bytes;
  }
  return undefined;
}

function parseOnePuxItem(item: JsonObject, files: OnePuxFiles): ImportedEntry {
  const category = asString(item['categoryUuid']);
  const type = ONEPASSWORD_CATEGORIES[category] ?? 'note';
  const typedMap = ONEPASSWORD_TYPED_FIELDS[category] ?? {};
  const labelMap = ONEPASSWORD_TYPED_LABELS[category] ?? {};
  const typed: Record<string, string> = { ...ONEPASSWORD_CATEGORY_FIELDS[category] };
  const overview = asObject(item['overview']) ?? {};
  const details = asObject(item['details']) ?? {};
  let username = '';
  let password = asString(details['password']);
  const extras: string[] = [];
  const customFields: EntryCustomField[] = [];
  // Document (categorie 006) et fichiers joints a un champ : pieces jointes chiffrees a l'import, jamais perdues
  // en silence -- un fichier annonce mais absent de l'archive est nomme dans les notes.
  const attachments: ImportedAttachment[] = [];
  const attachedIds = new Set<string>();
  const missingFiles: string[] = [];
  const attachDocument = (raw: Json | undefined): boolean => {
    const document = asObject(raw);
    if (document === null) return false;
    const documentId = asString(document['documentId']);
    const fileName = asString(document['fileName']) || 'document';
    if (attachedIds.has(documentId)) return true;
    const bytes = findOnePuxFile(files, documentId);
    if (bytes !== undefined && bytes.length > 0) {
      attachedIds.add(documentId);
      attachments.push({ fileName, bytes });
    } else {
      missingFiles.push(fileName);
    }
    return true;
  };
  attachDocument(details['documentAttributes']);
  for (const raw of asArray(details['loginFields'])) {
    const field = asObject(raw);
    if (field === null) continue;
    const value = asString(field['value']);
    const designation = asString(field['designation']);
    if (designation === 'username' && username === '') username = value;
    else if (designation === 'password' && password === '') password = value;
    // `fieldType` 'P' = champ mot de passe du formulaire : masque a l'ecran.
    else if (value.length > 0) customFields.push({ name: asString(field['name']), value, hidden: asString(field['fieldType']) === 'P' });
  }

  let totpSecret = '';
  let sshKey: SshKeyMaterial | null = null;
  // Champ `reference` (« Related items ») : l'identifiant d'un autre element de l'export, relie a l'import -- jamais
  // un champ personnalise fait d'un identifiant illisible.
  const linkedSourceIds: string[] = [];
  for (const rawSection of asArray(details['sections'])) {
    const section = asObject(rawSection);
    if (section === null) continue;
    for (const rawField of asArray(section['fields'])) {
      const field = asObject(rawField);
      const value = asObject(field?.['value']);
      if (field === null || value === null) continue;
      if (value['file'] !== undefined && attachDocument(value['file'])) continue;
      if (typeof value['reference'] === 'string') {
        if (value['reference'].length > 0 && !linkedSourceIds.includes(value['reference'])) linkedSourceIds.push(value['reference']);
        continue;
      }
      // Champ `sshKey` (categorie 114) : cle privee + `metadata.publicKey` -> paire de l'entree, jamais un champ en clair.
      const ssh = asObject(value['sshKey']);
      if (ssh !== null && sshKey === null) {
        const metadata = asObject(ssh['metadata']) ?? {};
        sshKey = { privateKey: asString(ssh['privateKey']) || asString(metadata['privateKey']), publicKey: asString(metadata['publicKey']) };
        continue;
      }
      const totp = value['totp'];
      if (typeof totp === 'string' && totp.length > 0) {
        const normalized = normalizeTotpSecret(totp);
        if (totpSecret === '' && normalized.secret.length > 0) totpSecret = normalized.secret;
        else extras.push(normalized.extraNote || `TOTP : ${totp}`);
        continue;
      }
      const address = asObject(value['address']);
      if (type === 'identity' && address !== null) {
        for (const [part, key] of Object.entries(ONEPASSWORD_ADDRESS_FIELDS)) {
          const text = asString(address[part]);
          if (text.length > 0 && typed[key] === undefined) typed[key] = text;
        }
        continue;
      }
      const text = describeFieldValue(value);
      const label = asString(field['title']).trim().toLowerCase();
      const isPasswordField = asString(field['id']) === ONEPASSWORD_PASSWORD_FIELDS[category] || label === ONEPASSWORD_PASSWORD_LABELS[category];
      if (isPasswordField && text.length > 0 && password === '') {
        password = text;
        continue;
      }
      if (asString(field['id']) === ONEPASSWORD_USERNAME_FIELDS[category] && text.length > 0 && username === '') {
        username = text;
        continue;
      }
      let typedKey = typedMap[asString(field['id'])] ?? labelMap[label];
      // Securite que Klavox ne sait pas ranger (WPA entreprise…) : champ personnalise, jamais jetee par la normalisation.
      if (typedKey === 'security' && wifiSecurity(text) === null) typedKey = undefined;
      if (typedKey === 'accountType' && bankAccountType(text) === null) typedKey = undefined;
      if (typedKey === 'engine' && databaseEngine(text) === null) typedKey = undefined;
      if (typedKey === 'incomingProtocol' && emailProtocol(text) === null) typedKey = undefined;
      if ((typedKey === 'incomingSecurity' || typedKey === 'smtpSecurity') && emailSecurity(text) === null) typedKey = undefined;
      // Premier champ non vide retenu (ex. plusieurs telephones) ; les suivants deviennent des champs personnalises.
      if (typedKey !== undefined && text.length > 0 && typed[typedKey] === undefined) {
        typed[typedKey] = text;
        continue;
      }
      if (text.length > 0) customFields.push({ name: asString(field['title']) || asString(field['id']), value: text, hidden: value['concealed'] !== undefined || value['sshKey'] !== undefined });
    }
  }

  const uris = asArray(overview['urls'])
    .map((raw) => asString(asObject(raw)?.['url']))
    .filter((url) => url.length > 0);
  const mainUrl = asString(overview['url']);
  if (mainUrl.length > 0 && !uris.includes(mainUrl)) uris.unshift(mainUrl);

  // `time` en secondes dans l'export 1Password ; une valeur sans date exploitable reste en notes plutot que perdue.
  const passwordHistory: PasswordHistoryEntry[] = [];
  const undatedHistory: string[] = [];
  for (const raw of asArray(details['passwordHistory'])) {
    const value = asString(asObject(raw)?.['value']);
    const time = asObject(raw)?.['time'];
    if (value.length === 0) continue;
    if (typeof time === 'number' && Number.isFinite(time) && time >= 0) passwordHistory.push({ password: value, changedAt: Math.round(time * 1000) });
    else undatedHistory.push(value);
  }
  // 1Password garde chaque ancien mot de passe : sans historique, le mot de passe date de la creation de l'element
  // (`createdAt`, en secondes) ; avec, c'est l'historique qui date son dernier changement.
  const createdAt = item['createdAt'];
  const passwordChangedAt = typeof createdAt === 'number' && Number.isFinite(createdAt) && createdAt > 0 ? Math.round(createdAt * 1000) : null;
  if (undatedHistory.length > 0) extras.push(`Anciens mots de passe : ${undatedHistory.join(', ')}`);
  if (missingFiles.length > 0) extras.push(`Fichier(s) absent(s) de l'export 1Password : ${missingFiles.join(', ')}`);

  const tags = normalizeTags(asArray(overview['tags']).map(asString));

  const group = typedFieldGroup(type);
  return {
    type,
    name: asString(overview['title']) || uris[0] || username || attachments[0]?.fileName || 'Sans nom',
    username,
    password,
    uris,
    notes: joinNotes([asString(details['notesPlain']), ...extras]),
    totpSecret,
    favorite: typeof item['favIndex'] === 'number' && item['favIndex'] > 0,
    // `state: archived` : l'element est aux Archives de 1Password, il y reste (`entry-archive.ts`).
    ...(item['state'] === 'archived' ? { archived: true } : {}),
    ...(tags.length > 0 ? { tags } : {}),
    ...(passwordHistory.length > 0 ? { passwordHistory } : {}),
    ...(passwordChangedAt !== null ? { passwordChangedAt } : {}),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(group !== null ? { fields: normalizeTypedFields(group, typed) } : {}),
    ...(sshKey !== null ? { sshKey } : {}),
    ...(attachments.length > 0 ? { attachments } : {}),
    ...(asString(item['uuid']).length > 0 ? { sourceId: asString(item['uuid']) } : {}),
    ...(linkedSourceIds.length > 0 ? { linkedSourceIds } : {}),
  };
}

/**
 * Parse le JSON `export.data` d'un `.1pux` (comptes > coffres > elements).
 * Les elements mis a la corbeille sont ignores ; les archives sont importes.
 * `files` = dossier `files/` de l'archive : documents et fichiers joints y sont pris
 * comme pieces jointes de leur element.
 *
 * @throws {Error} JSON illisible ou structure sans comptes
 */
export function parseOnePuxExportData(text: string, files: OnePuxFiles = new Map()): ImportedEntry[] {
  let data: Json;
  try {
    data = JSON.parse(text) as Json;
  } catch {
    throw new Error('export 1Password illisible');
  }
  const accounts = asObject(data)?.['accounts'];
  if (!Array.isArray(accounts)) throw new Error('export 1Password invalide (aucun compte)');
  const entries: ImportedEntry[] = [];
  for (const account of accounts) {
    for (const vault of asArray(asObject(account)?.['vaults'])) {
      for (const raw of asArray(asObject(vault)?.['items'])) {
        const item = asObject(raw);
        // Selon la version de l'export, l'element est direct ou enveloppe dans `item`.
        const resolved = asObject(item?.['item']) ?? item;
        if (resolved === null || resolved['trashed'] === true || resolved['state'] === 'trashed') continue;
        entries.push(parseOnePuxItem(resolved, files));
      }
    }
  }
  return entries;
}

/**
 * Parse un export 1Password `.1pif` : chaque element, ramene a la forme `.1pux`, passe par le meme lecteur que
 * l'export moderne, dans le dossier ou il etait range. Le `.1pif` ne porte pas le contenu des documents : un
 * document annonce est nomme dans les notes.
 */
export function parseOnePifExport(text: string): ImportedEntry[] {
  return onePifItems(text).map(({ item, folderPath }) => {
    const entry = parseOnePuxItem(item as JsonObject, new Map());
    return folderPath.length > 0 ? { ...entry, folderPath } : entry;
  });
}

export interface ImportFileResult {
  readonly entries: ImportedEntry[];
  /** Fichiers d'une archive sans colonne exploitable, signales a l'utilisateur plutot que perdus en silence. */
  readonly skippedFiles: string[];
  /**
   * Dossiers declares par l'export, VIDES compris (Bitwarden JSON, groupes KeePass, coffres Proton Pass, dossiers
   * Delinea) : recrees a l'import comme chez Bitwarden, sinon un dossier prepare sans entree disparait au demenagement.
   * Absent : seuls ceux des entrees.
   */
  readonly folders?: readonly (readonly string[])[];
}

export interface ImportFileOptions {
  /**
   * Mot de passe de l'export chiffre (base KDBX, export Bitwarden protege,
   * sauvegarde Aegis, 2FAS ou Proton Authenticator chiffree).
   * Fourni par l'UI apres un `ImportPasswordRequiredError` ; jamais conserve.
   */
  readonly password?: string;
  /** Fichier-cle KeePass optionnel, choisi par l'utilisateur a cote de la base. */
  readonly keyFile?: Uint8Array | null;
  /**
   * Avancement de la derivation de cle d'un export chiffre (base KeePass, export Bitwarden protege, sauvegarde Aegis,
   * 2FAS ou Proton Authenticator chiffree, export CXF Klavox) (0..1), affiche sur le bouton d'import.
   */
  readonly onProgress?: (fraction: number) => void;
  /**
   * Format choisi a l'ecran d'import pour un export que son contenu ne trahit
   * pas (CSV sans en-tete, `headerless-import.ts`) ; absent ou `auto` = reconnu au contenu.
   */
  readonly format?: ImportFormat;
}

/** Suggestion ajoutee a un CSV illisible : c'est souvent un export sans en-tete, que seul le choix du format ouvre. */
const HEADERLESS_SUGGESTION =
  ' -- export sans en-tete (Universal Password Manager, Password Agent, PasswordWallet, SplashID, Ascendo DataVault) : choisissez son format avant le fichier';

/** Aiguille un JSON d'export concurrent vers le lecteur du bon gestionnaire. */
async function parseImportJson(text: string, options: ImportFileOptions): Promise<ImportedEntry[] | Omit<ImportFileResult, 'skippedFiles'>> {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('fichier JSON illisible');
  }
  if ((data as { format?: unknown } | null)?.format === VAULT_EXPORT_FORMAT) {
    throw new Error('export Klavox : utilisez « Importer un export Klavox » (il est chiffre, il ne se lit pas ici)');
  }
  // Le CXF passe AVANT 1Password : un `.1pux` porte aussi un tableau
  // `accounts`, seul le couple `exporter` + `accounts` distingue le CXF.
  if (isCxfExportFile(data) || isCxfPayload(data)) return parseCxfImport(data, options.password, undefined, options.onProgress);
  // Authentificateurs (graines 2FA) : enveloppes propres, sans cle commune avec un gestionnaire.
  if (isAegisExport(data)) return parseAegisExport(data, options.password, options.onProgress);
  if (isTwoFasExport(data)) return parseTwoFasExport(data, options.password, options.onProgress);
  if (isProtonAuthenticatorExport(data)) return parseProtonAuthenticatorExport(data, options.password, options.onProgress);
  if (isProtonExport(data as never)) return readProtonExport(data as never);
  // Psono passe AVANT Bitwarden : tous deux portent un tableau `items`.
  if (isPsonoExport(data)) return parsePsonoExport(data);
  // Password Boss passe AVANT Bitwarden : tous deux portent un tableau `items`.
  if (isPasswordBossExport(data)) return parsePasswordBossExport(data);
  // Enpass passe AVANT Bitwarden : tous deux portent un tableau `items`.
  if (isEnpassExport(data)) return parseEnpassExport(data);
  // Passky passe AVANT Bitwarden : tous deux portent un drapeau `encrypted`.
  if (isPasskyExport(data)) return parsePasskyExport(data);
  // F-Secure passe AVANT Bitwarden : tous deux portent une cle `data` (objet chez F-Secure, chaine chez Bitwarden).
  if (isFSecureExport(data)) return parseFSecureExport(data);
  if (isBitwardenExport(data as never)) return readBitwardenExport(data as never, options.password, options.onProgress);
  if (isKeeperExport(data)) return parseKeeperExport(data);
  if (isAvastExport(data)) return parseAvastExport(data);
  if (isPassmanExport(data)) return parsePassmanExport(data);
  if (isGnomeKeyringExport(data)) return parseGnomeKeyringExport(data);
  if (isDashlaneJsonExport(data)) return parseDashlaneJsonExport(data);
  throw new Error('format JSON non reconnu (Bitwarden, Proton Pass, Enpass, Keeper, Avast, Psono, Password Boss, Passky, F-Secure, Passman, GNOME Passwords, Dashlane, Aegis, 2FAS, Proton Authenticator ou FIDO CXF attendu)');
}

function jsonResult(read: ImportedEntry[] | Omit<ImportFileResult, 'skippedFiles'>): ImportFileResult {
  return Array.isArray(read) ? { entries: read, skippedFiles: [] } : { ...read, skippedFiles: [] };
}

/**
 * Point d'entree unique de l'import concurrent : aiguille selon le contenu.
 * KDBX -> KeePass ; `.kdb` -> KeePass 1.x ; `.1pux` / ZIP contenant `export.data` -> 1Password ; `.1pif` (objets JSON separes par
 * `***<uuid>***`, seul ou dans un ZIP) -> 1Password 4 a 7 ; ZIP contenant `data.json` -> Proton Pass ; autre ZIP -> chaque `.csv`
 * (Dashlane) ; liste d'URI `otpauth://` / `otpauth-migration://` -> authentificateur ; JSON -> FIDO CXF, Aegis, 2FAS, Proton Authenticator, Bitwarden, Proton Pass, Enpass, Keeper, Avast, Psono, Password Boss, Passky, F-Secure, Passman, GNOME Passwords ou Dashlane ;
 * page HTML -> Clipperz ; XML -> KeePass (export « KeePass XML (2.x) »), Sticky Password, Password Depot, SafeInCloud, Password Safe, Delinea Secret Server ou Password Dragon ; CSV a `;` Password XP ; sinon texte CSV.
 * Un format CHOISI (`options.format`, exports sans en-tete et BlackBerry Password Keeper) court-circuite toute reconnaissance.
 *
 * @throws {ImportPasswordRequiredError} export chiffre : l'UI demande le mot de passe puis rappelle
 */
export async function parseImportFile(
  fileName: string,
  bytes: Uint8Array,
  options: ImportFileOptions = {},
): Promise<ImportFileResult> {
  if (isKdbxFile(bytes)) {
    return { ...(await readKdbxFile(bytes, { password: options.password, keyFile: options.keyFile ?? null, onProgress: options.onProgress })), skippedFiles: [] };
  }
  if (isKdbFile(bytes)) {
    return { ...(await readKdbFile(bytes, { password: options.password, keyFile: options.keyFile ?? null, onProgress: options.onProgress })), skippedFiles: [] };
  }
  const isZip = bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
  if (options.format !== undefined && options.format !== 'auto') {
    // Format choisi par l'utilisateur : il PRIME sur toute reconnaissance, jamais devine a sa place.
    if (isZip) throw new Error('le format choisi attend un fichier texte (CSV ou TXT), pas une archive');
    return { entries: parseExplicitFormat(options.format, new TextDecoder().decode(bytes)), skippedFiles: [] };
  }
  if (!isZip) {
    if (fileName.toLowerCase().endsWith('.1pux')) throw new Error('export 1Password invalide (archive attendue)');
    const text = new TextDecoder().decode(bytes);
    // Le `.1pif` passe AVANT le JSON : il commence par `{` mais aligne plusieurs objets, que `JSON.parse` refuse.
    if (isOnePifText(text)) return { entries: parseOnePifExport(text), skippedFiles: [] };
    if (isAuthenticatorUriList(text)) return { entries: parseAuthenticatorUriList(text), skippedFiles: [] };
    if (/^[\s\uFEFF]*[{[]/u.test(text)) return jsonResult(await parseImportJson(text, options));
    if (isClipperzHtml(text)) return { entries: parseClipperzHtml(text), skippedFiles: [] };
    if (isKasperskyTxt(text)) return { entries: parseKasperskyTxt(text), skippedFiles: [] };
    if (isKeePassXml(text)) return { ...(await readKeePassXml(text)), skippedFiles: [] };
    if (isStickyPasswordXml(text)) return { entries: parseStickyPasswordXml(text), skippedFiles: [] };
    if (isPasswordDepotXml(text)) return { entries: parsePasswordDepotXml(text), skippedFiles: [] };
    if (isSafeInCloudXml(text)) return { entries: await parseSafeInCloudXml(text), skippedFiles: [] };
    if (isPasswordSafeXml(text)) return { entries: parsePasswordSafeXml(text), skippedFiles: [] };
    if (isDelineaXml(text)) return { ...readDelineaXml(text), skippedFiles: [] };
    if (isPasswordDragonXml(text)) return { entries: parsePasswordDragonXml(text), skippedFiles: [] };
    if (isPasswordXpCsv(text)) return { entries: parsePasswordXpCsv(text), skippedFiles: [] };
    try {
      return { entries: parseLoginCsv(text), skippedFiles: [] };
    } catch (cause) {
      if (cause instanceof Error && cause.message.startsWith('aucune colonne reconnue')) throw new Error(cause.message + HEADERLESS_SUGGESTION);
      throw cause;
    }
  }
  const files = await readZipEntries(bytes);
  const onePuxData = files.get('export.data');
  if (onePuxData !== undefined) {
    return { entries: parseOnePuxExportData(new TextDecoder().decode(onePuxData), files), skippedFiles: [] };
  }
  // Dossier `.1pif` de 1Password 4 a 7 compresse par l'utilisateur : `<coffre>.1pif/data.1pif`.
  for (const [name, content] of files) {
    if (!name.toLowerCase().endsWith('.1pif')) continue;
    return { entries: parseOnePifExport(new TextDecoder().decode(content)), skippedFiles: [] };
  }
  // Proton Pass exporte `Proton Pass/data.json` dans son archive.
  for (const [name, content] of files) {
    if (!name.toLowerCase().endsWith('.json')) continue;
    return jsonResult(await parseImportJson(new TextDecoder().decode(content), options));
  }
  const entries: ImportedEntry[] = [];
  const skippedFiles: string[] = [];
  for (const [name, content] of files) {
    if (!name.toLowerCase().endsWith('.csv')) continue;
    try {
      entries.push(...parseLoginCsv(new TextDecoder().decode(content)));
    } catch {
      skippedFiles.push(name);
    }
  }
  if (entries.length === 0 && skippedFiles.length === 0) throw new Error('archive sans export reconnu');
  return { entries, skippedFiles };
}
